import { FunctionComponent } from 'react';

import styled, { keyframes } from 'styled-components';

import { FieldContainer, GridColumn } from 'core/forms';
import { Page, PageHeader } from 'core/ui';

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const SkeletonBase = styled.div`
  background: #eee;
  background-image: linear-gradient(90deg, #eee, #f5f5f5, #eee);
  background-size: 200px 100%;
  background-repeat: no-repeat;
  border-radius: 4px;
  animation: ${shimmer} 1.3s ease-in-out infinite;
`;

const SkeletonField = styled(SkeletonBase)`
  height: 40px;
  width: 100%;
`;

const SkeletonLabel = styled(SkeletonBase)`
  height: 16px;
  width: 80px;
  margin-bottom: 8px;
`;

const SkeletonButton = styled(SkeletonBase)`
  height: 36px;
  width: 80px;
`;

const StyledForm = styled.form`
  display: grid;
  grid-template-columns: 200px 1fr;
  grid-gap: 20px;
  padding: 20px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin: 20px;
`;

const StyledPage = styled(Page)`
  max-width: 1200px;
  margin: 0 auto;
`;

const StyledActionsColumn = styled(GridColumn)`
  display: flex;
  justify-content: flex-start;
  gap: 10px;
  margin-top: 20px;
`;

const LoadingMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 20px;
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 4px;
  border-left: 4px solid #007bff;
  font-size: 14px;
  color: #495057;
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid #dee2e6;
  border-top: 2px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

export interface WorklistViewUserFormSkeletonProps {
  /** Loading message to display */
  loadingMessage?: string;
  /** Whether this is for a new or edit form */
  isEditMode?: boolean;
}

/**
 * Skeleton loader for WorklistViewUserForm
 *
 * Provides a smooth loading experience with animated placeholders
 * that match the actual form structure, preventing layout shifts
 * and giving users visual feedback during data loading.
 */
export const WorklistViewUserFormSkeleton: FunctionComponent<WorklistViewUserFormSkeletonProps> = ({
  loadingMessage = 'Loading form data...',
  isEditMode = false,
}) => {
  return (
    <StyledPage>
      <PageHeader title={isEditMode ? 'Edit Worklist Access Rule' : 'New Worklist Access Rule'} />

      <LoadingMessage>
        <LoadingSpinner />
        {loadingMessage}
      </LoadingMessage>

      <StyledForm>
        {/* Worklist Field */}
        <GridColumn columnStart="1" isLabelColumn>
          <SkeletonLabel />
        </GridColumn>
        <GridColumn columnStart="2">
          <FieldContainer $hideLabel>
            <SkeletonField />
          </FieldContainer>
        </GridColumn>

        {/* Access Field */}
        <GridColumn columnStart="1" isLabelColumn>
          <SkeletonLabel />
        </GridColumn>
        <GridColumn columnStart="2">
          <FieldContainer $hideLabel>
            <SkeletonField />
          </FieldContainer>
        </GridColumn>

        {/* Default Switch */}
        <GridColumn columnStart="1" isLabelColumn>
          <SkeletonLabel />
        </GridColumn>
        <GridColumn columnStart="2">
          <FieldContainer $hideLabel>
            <SkeletonField style={{ width: '60px', height: '24px' }} />
          </FieldContainer>
        </GridColumn>

        {/* Active Switch */}
        <GridColumn columnStart="1" isLabelColumn>
          <SkeletonLabel />
        </GridColumn>
        <GridColumn columnStart="2">
          <FieldContainer $hideLabel>
            <SkeletonField style={{ width: '60px', height: '24px' }} />
          </FieldContainer>
        </GridColumn>

        {/* User Field */}
        <GridColumn columnStart="1" isLabelColumn>
          <SkeletonLabel />
        </GridColumn>
        <GridColumn columnStart="2">
          <FieldContainer $hideLabel>
            <SkeletonField />
          </FieldContainer>
        </GridColumn>

        {/* Location Field */}
        <GridColumn columnStart="1" isLabelColumn>
          <SkeletonLabel />
        </GridColumn>
        <GridColumn columnStart="2">
          <FieldContainer $hideLabel>
            <SkeletonField />
          </FieldContainer>
        </GridColumn>

        {/* Account Field */}
        <GridColumn columnStart="1" isLabelColumn>
          <SkeletonLabel />
        </GridColumn>
        <GridColumn columnStart="2">
          <FieldContainer $hideLabel>
            <SkeletonField />
          </FieldContainer>
        </GridColumn>

        {/* Action Buttons */}
        <StyledActionsColumn columnStart="1" columnEnd="span 2">
          {isEditMode && <SkeletonButton />}
          <SkeletonButton />
        </StyledActionsColumn>
      </StyledForm>
    </StyledPage>
  );
};
